import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Loader2,
  FlaskConical,
  Trash2,
  Stethoscope,
  Package,
  AlertTriangle,
  StickyNote,
  FileText,
  Image as ImageIcon,
  X,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// GHS pictograms with short human-readable name (fallback icon color)
const GHS_MAP = {
  GHS01: { label: "Esplosivo", color: "bg-rose-100 text-rose-700 border-rose-300" },
  GHS02: { label: "Infiammabile", color: "bg-orange-100 text-orange-700 border-orange-300" },
  GHS03: { label: "Comburente", color: "bg-amber-100 text-amber-700 border-amber-300" },
  GHS04: { label: "Gas sotto pressione", color: "bg-sky-100 text-sky-700 border-sky-300" },
  GHS05: { label: "Corrosivo", color: "bg-red-100 text-red-800 border-red-300" },
  GHS06: { label: "Tossicità acuta", color: "bg-rose-100 text-rose-800 border-rose-300" },
  GHS07: { label: "Irritante", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  GHS08: { label: "Pericoloso per la salute", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  GHS09: { label: "Pericoloso per l'ambiente", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

function MoVaRisChBadge({ level }) {
  const cls =
    level === "alto"
      ? "bg-rose-100 text-rose-700 border-rose-300"
      : level === "moderato"
      ? "bg-amber-100 text-amber-700 border-amber-300"
      : level === "basso"
      ? "bg-yellow-100 text-yellow-800 border-yellow-300"
      : "bg-emerald-100 text-emerald-700 border-emerald-300";
  return (
    <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>
      {level || "—"}
    </span>
  );
}

function AiSourcesBadge({ sources, gptNote }) {
  if (!sources) return null;
  const added = sources.openai_added || [];
  const modified = sources.openai_modified || [];
  const err = sources.openai_error;
  const models = sources.models || {};
  return (
    <div className="mt-2 border border-indigo-200 bg-indigo-50 rounded p-2 text-xs">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-semibold">Dual-AI</span>
        <span className="text-indigo-900 font-semibold">
          Gemini{models.first_pass ? ` (${models.first_pass})` : ""} → GPT{models.critique ? ` (${models.critique})` : ""}
        </span>
        {err && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-200 text-amber-900" title={err}>
            GPT non disponibile — solo Gemini
          </span>
        )}
      </div>
      {(added.length > 0 || modified.length > 0) && (
        <div className="mt-1 text-[11px] text-indigo-900">
          {added.length > 0 && <>GPT ha aggiunto: <b>{added.join(", ")}</b>. </>}
          {modified.length > 0 && <>GPT ha corretto: <b>{modified.join(", ")}</b>.</>}
        </div>
      )}
      {gptNote && (
        <div className="mt-1 text-[11px] italic text-indigo-900/80">GPT-5.5: {gptNote}</div>
      )}
    </div>
  );
}

function fileKindIcon(name) {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".pdf") || n.endsWith(".doc") || n.endsWith(".docx")) return <FileText className="w-4 h-4" />;
  return <ImageIcon className="w-4 h-4" />;
}

export default function StepSostanze({ data, onChange, aiLoading, setAiLoading }) {
  const list = Array.isArray(data) ? data : [];
  const fileRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0, currentName: "" });

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setPendingFiles((prev) => [...prev, ...arr]);
  };

  const removePending = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const analyze = async () => {
    if (!pendingFiles.length) return toast.error("Carica almeno un file (etichetta o SDS)");
    setAiLoading(true);
    const trimmedNote = note.trim();
    // Sequential loop: 1 file per request avoids Cloudflare ingress timeout (~100s)
    // and lets us show per-file progress + append results incrementally.
    const total = pendingFiles.length;
    setProgress({ done: 0, total, currentName: pendingFiles[0]?.name || "" });
    const added = [];
    const errs = [];
    let current = [...list];
    for (let i = 0; i < pendingFiles.length; i++) {
      const f = pendingFiles[i];
      setProgress({ done: i, total, currentName: f.name });
      const fd = new FormData();
      fd.append("files", f);
      if (trimmedNote) fd.append("note", trimmedNote);
      try {
        const r = await api.post("/dvr/ai/chemical", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        });
        const payload = r.data || {};
        const subs = Array.isArray(payload.substances) ? payload.substances : [];
        const fileErrs = Array.isArray(payload.errors) ? payload.errors : [];
        subs.forEach((s) => {
          const out = { ...s, _uid: uid() };
          if (trimmedNote && !out._operator_note) out._operator_note = trimmedNote;
          added.push(out);
          current = [...current, out];
        });
        // Incremental UI update so the user sees results as they arrive
        onChange(current);
        fileErrs.forEach((e) => errs.push(e));
      } catch (err) {
        errs.push({ file: f.name, error: err.response?.data?.detail || err.message || "AI error" });
      }
    }
    setProgress({ done: total, total, currentName: "" });
    if (added.length) {
      toast.success(`${added.length}/${total} sostanza/e aggiunta/e`);
      setPendingFiles([]);
      setNote("");
    }
    if (errs.length) {
      toast.warning(`${errs.length} file non processato/i: ${errs.map((e) => e.file).join(", ")}`);
    }
    if (!added.length && !errs.length) {
      toast.error("Nessuna sostanza estratta");
    }
    setAiLoading(false);
    setTimeout(() => setProgress({ done: 0, total: 0, currentName: "" }), 800);
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid="step-sostanze">
      <p className="text-sm text-muted-foreground">
        Carica <b>etichette CLP</b> (foto JPG/PNG) e/o <b>Schede di Sicurezza (SDS)</b> in formato
        PDF o Word. Puoi selezionare <b>più file contemporaneamente</b>: ogni file diventa una sostanza.
        <br />
        <b>Analisi Dual-AI</b>: Gemini 3 Pro estrae dati CLP e valuta MoVaRisCh, poi GPT-5.5 verifica
        e arricchisce (D.Lgs. 81/08 Titolo IX, Reg. CE 1272/2008, Reg. UE 2020/878). ~30-60s per file.
      </p>

      <button
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-border rounded-md p-6 text-sm hover:bg-muted/30 w-full"
        data-testid="s7-upload-label"
      >
        <FlaskConical className="w-6 h-6 mx-auto mb-1" />
        {pendingFiles.length
          ? `${pendingFiles.length} file selezionato/i — clicca per aggiungerne altri`
          : "Carica etichette CLP (foto) o Schede di Sicurezza (PDF/DOCX)"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        hidden
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {pendingFiles.length > 0 && (
        <div className="border border-border rounded-md p-3 bg-muted/20 space-y-1.5" data-testid="s7-pending-list">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">File in coda</div>
          {pendingFiles.map((f, i) => (
            <div key={`pending-${i}-${f.name}`} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0">
                {fileKindIcon(f.name)}
                <span className="truncate">{f.name}</span>
                <span className="text-muted-foreground shrink-0">({Math.max(1, Math.round(f.size / 1024))} KB)</span>
              </div>
              <button
                onClick={() => removePending(i)}
                className="text-muted-foreground hover:text-destructive p-1"
                data-testid={`s7-pending-remove-${i}`}
                type="button"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <Label className="text-xs flex items-center gap-1">
          <StickyNote className="w-3 h-3" /> Note operatore (uso, ambiente, dettagli non visibili in etichetta/SDS)
        </Label>
        <Textarea
          rows={3}
          placeholder="Es. utilizzo in ambiente ventilato per 30 min/giorno; travasi manuali; scorta in armadio ventilato…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="s7-input-note"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          Le note vengono trasmesse all'AI per una valutazione MoVaRisCh più contestualizzata e applicate a
          <b> tutte</b> le sostanze in coda.
        </p>
      </div>
      <Button
        onClick={analyze}
        disabled={aiLoading || !pendingFiles.length}
        className="rounded-full"
        data-testid="s7-analyze"
      >
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        {aiLoading && progress.total > 1
          ? `Analisi ${progress.done + 1}/${progress.total} — ${progress.currentName?.slice(0, 30) || ""}…`
          : aiLoading
          ? "Analisi in corso…"
          : `Analizza ${pendingFiles.length || ""} file (Gemini + GPT-5.5)`}
      </Button>

      {aiLoading && progress.total > 1 && (
        <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden" data-testid="s7-progress-bar">
          <div
            className="h-full bg-indigo-600 transition-all duration-300"
            style={{ width: `${(progress.done / progress.total) * 100}%` }}
          />
        </div>
      )}

      <div className="space-y-3">
        {list.map((s, i) => {
          const p = s.prodotto || {};
          const mv = s.movarisch || {};
          const cardKey = s._uid || `sost-${i}`;
          return (
            <div key={cardKey} className="border border-border rounded-md p-4 bg-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FlaskConical className="w-4 h-4 text-muted-foreground" />
                    {p.nome || "Sostanza"}
                  </h3>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {p.produttore && <>Produttore: <b>{p.produttore}</b></>}
                    {p.codice && <> · Codice: {p.codice}</>}
                  </div>
                  {p.classificazione_clp && (
                    <div className="text-xs text-muted-foreground mt-0.5">CLP: {p.classificazione_clp}</div>
                  )}
                  {s._source_file?.name && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      {fileKindIcon(s._source_file.name)}
                      <span className="truncate">Fonte: {s._source_file.name}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-destructive"
                  data-testid={`s7-remove-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {s.photos?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {s.photos.map((u) => (
                    <img
                      key={`photo-${u}`}
                      src={`${BACKEND}${u}`}
                      alt=""
                      className="w-28 h-28 object-cover rounded border border-border"
                    />
                  ))}
                </div>
              )}

              {s.sds_files?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.sds_files.map((f) => (
                    <a
                      key={`sds-${f.url}`}
                      href={`${BACKEND}${f.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border bg-muted/40 hover:bg-muted"
                    >
                      <FileText className="w-3 h-3" /> SDS {f.ext?.toUpperCase()} — {f.name}
                    </a>
                  ))}
                </div>
              )}

              <AiSourcesBadge sources={s._ai_sources} gptNote={s._gpt_note} />

              {s._operator_note && (
                <div className="mt-3 border-l-2 border-amber-400 bg-amber-50/50 pl-3 py-2 rounded-r text-sm">
                  <div className="text-[11px] uppercase tracking-widest text-amber-800 flex items-center gap-1">
                    <StickyNote className="w-3 h-3" /> Note operatore
                  </div>
                  <div className="mt-1 whitespace-pre-wrap">{s._operator_note}</div>
                </div>
              )}

              {s.pittogrammi?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Pittogrammi CLP</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {s.pittogrammi.map((g) => {
                      const meta = GHS_MAP[g] || { label: g, color: "bg-slate-100 text-slate-700 border-slate-300" };
                      return (
                        <span key={`ghs-${g}`} className={`text-xs px-2 py-1 rounded border ${meta.color}`}>
                          <b>{g}</b> — {meta.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {s.frasi_h?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Frasi H</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.frasi_h.map((h) => (
                        <code key={`h-${h}`} className="text-[11px] bg-muted/50 px-1.5 py-0.5 rounded">{h}</code>
                      ))}
                    </div>
                  </div>
                )}
                {s.frasi_p?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Frasi P</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.frasi_p.map((p_) => (
                        <code key={`p-${p_}`} className="text-[11px] bg-muted/50 px-1.5 py-0.5 rounded">{p_}</code>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {Object.keys(mv).length > 0 && (
                <div className="mt-3 border border-border rounded-md p-3 bg-muted/20">
                  <div className="flex items-center gap-2">
                    <b className="text-sm">Valutazione MoVaRisCh</b>
                    <MoVaRisChBadge level={mv.livello} />
                  </div>
                  <div className="text-xs mt-1 text-muted-foreground">
                    P<sub>chim</sub>={mv.Pchim ?? "?"} · I<sub>chim</sub>={mv.Ich ?? "?"} · R<sub>chim</sub>={mv.Rchim ?? "?"}
                  </div>
                </div>
              )}

              {s.dpi?.length > 0 && (
                <div className="mt-3 text-sm">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">DPI</div>
                  <div className="mt-1">{s.dpi.join(" · ")}</div>
                </div>
              )}

              {s.stoccaggio && (
                <div className="mt-3 text-sm">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <Package className="w-3 h-3" /> Stoccaggio
                  </div>
                  <div className="mt-1">{s.stoccaggio}</div>
                </div>
              )}

              {s.procedure_emergenza && (
                <div className="mt-3 border border-amber-200 bg-amber-50 rounded-md p-3 text-sm">
                  <div className="text-[11px] uppercase tracking-widest text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Procedure di emergenza
                  </div>
                  <div className="mt-1 text-amber-900">{s.procedure_emergenza}</div>
                </div>
              )}

              {s.sorveglianza_sanitaria?.obbligo && (
                <div className="mt-3 border border-rose-200 bg-rose-50 rounded-md p-3 text-sm">
                  <div className="text-[11px] uppercase tracking-widest text-rose-800 flex items-center gap-1">
                    <Stethoscope className="w-3 h-3" /> Sorveglianza sanitaria: OBBLIGATORIA
                  </div>
                  {s.sorveglianza_sanitaria.esami?.length > 0 && (
                    <div className="mt-1 text-rose-900">Esami: {s.sorveglianza_sanitaria.esami.join(", ")}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-6 text-center">
            Nessuna sostanza ancora analizzata.
          </div>
        )}
      </div>
    </div>
  );
}
